import DOMPurify from 'dompurify'
import GD from '../GD'
import { BlackHoleMethods, WSMessage } from '../enums'
import { LocalStorage, LS, DCCAPI } from '../utils'
import { codeHTML, codeLineInnerHTML, codeLinesHTML, quoteHTML } from '../utils/Regex'
import { Config } from './index'

class Chat {
  private ls: LocalStorage = new LocalStorage()
  private chat: ChatVO | undefined
  private sharedObject: IShared = window.DCC_SHARED
  private auth: { profile: IProfile, key: string } | undefined
  private isTypingTimer: ReturnType<typeof setTimeout> | null

  constructor() {
    GD.AUTH_COMPLETE.subscribe(async (data) => {
      this.auth = data
      this.checkIfHadPendingMsgs()
      await this.openChat()
    })

    GD.INVOKE_DELAYED_EVENTS.subscribe((packets: DelayedPacket[]) => {
      // TODO? parse different types of methods
      const msgAddPackets = packets.filter(
        (packet: DelayedPacket) => packet.method === WSMessage.MESSAGE_ADD
      )
      const userEnteredPacket = packets.find(
        (packet: DelayedPacket) => packet.method === WSMessage.CHAT_USER_ENTER
      )

      if (msgAddPackets.length) {
        setTimeout(() => {
          msgAddPackets.forEach((msg: { data }) => {
            GD.WS_SEND.invoke({
              method: WSMessage.MESSAGE_ADD,
              data: msg.data
            })
          })
        })
      }
      if (userEnteredPacket) {
        setTimeout(() => {
          GD.WS_SEND.invoke({
            method: WSMessage.CHAT_USER_ENTER,
            data: {
              chatUID: userEnteredPacket.data.chatUID
            }
          })
        })
      }
    })

    GD.GUI_CLOSE_CHAT.subscribe(() => {
      this.closeChat()
    })

    GD.CLOSE_CHAT.subscribe(() => {
      this.closeChat()
    })

    GD.SET_CHAT.subscribe((cvo) => {
      if (this.chat) {
        GD.CLOSE_CHAT.invoke()
      }

      this.chat = cvo

      if (cvo.pointID && cvo.pointID > 0) {
        GD.LOG.invoke('Start entry point: ' + cvo.pointID)
        this.sendEntryPointStart('EP START')
      }

      GD.WS_SEND.invoke({
        method: WSMessage.CHAT_USER_ENTER,
        data: {
          chatUID: this.chat?.uid,
        },
      })
    })

    GD.GET_CHAT.listener = async (): Promise<ChatVO> => {
      return this.chat as ChatVO
    }

    GD.CHAT_MESSAGE_SEND_REQUEST.subscribe(async (txt) => {
      if (!this.chat) {
        GD.ALERT_ERROR.invoke('Chat not loaded yet!')
        return
      }
      const msg = await this.prepareMessage(txt)
      if (!msg) return
      // 3. send message
      GD.WS_SEND.invoke({
        method: WSMessage.MESSAGE_ADD,
        data: {
          chatUID: this.chat.uid,
          text: msg
        }
      })
      // 4. send epStarted
      if (this.chat.pointID && this.chat.pointID > 0) this.sendEntryPointStart(msg)
    })

    GD.CHAT_BOT_COMMAND_SEND_REQUEST.subscribe((req) => {
      if (!this.chat) return
      let msg: string = JSON.stringify({
        method: 'botCommand',
        type: 'chatSystem',
        title: req.title,
        action: req.action,
        actionData: req.actionData
      })
      msg = Config.systemSequence + msg
      const textToSend: string =
        '^' + req.botUID + '^' + DCCAPI.pack(msg, Config.companySequence)
      GD.WS_SEND.invoke({
        method: WSMessage.MESSAGE_ADD,
        data: {
          chatUID: this.chat?.uid,
          text: textToSend
        }
      })
    })

    GD.SEND_INPUT_ACTIVITY.subscribe((packet) => {
      // Вызывается на кликах фокусе и блюре инпут поля
      // Когда нажимается на input поле то в пакете - isTyping = true, когда блюр - isTyping = false
      const { isTyping } = packet
      // Если произошел блюр то:
      if (!isTyping && this.isTypingTimer) {
        clearTimeout(this.isTypingTimer)
        this.isTypingTimer = null
        return
      }
      // Если произошел фокус то:
      this.informAboutTyping()

      if (this.isTypingTimer) {
        clearTimeout(this.isTypingTimer)
      }

      this.isTypingTimer = setTimeout(() => {
        this.informAboutTyping()
      }, 30000)
    })

    GD.SERVICE_READY.invoke('chat')
  }

  private async openChat() {
    const epID = this.sharedObject.ep
    let promptMessage = 'Type your message...'

    if (this.sharedObject.promptMessage) promptMessage = this.sharedObject.promptMessage

    const resp = await GD.REQ_HTTP.request({
      method: 'company.startChat',
      epID: epID,
      guestUID: this.auth?.profile.type === 'guest' ? this.auth.profile.uid : null,
    })

    if (resp.error) {
      GD.ALERT_ERROR.invoke('Can\'t open chat, \n' + resp.errorMsg)
      return
    }

    if (!resp.data) {
      GD.ALERT_ERROR.invoke('Can\'t open chat, no chat found')
      return
    }

    const chat = resp.data as ChatVO

    chat.securityKey = Config.companySequence
    chat.promptMessage = promptMessage
    if (this.sharedObject.title) chat.title = this.sharedObject.title

    GD.SET_CHAT.invoke(chat)
  }

  private closeChat(): void {
    GD.WS_SEND.invoke({
      method: WSMessage.CHAT_USER_EXIT,
      data: {
        chatUID: this.chat?.uid
      }
    })
  }

  private sendEntryPointStart = (msg: string | null) => {
    if (!this.chat) return
    GD.WS_SEND.invoke({
      method: 'entryPointStart',
      data: {
        chatUID: this.chat?.uid,
        text: msg,
        pid: this.chat.pointID
      }
    })
  }

  private checkIfHadPendingMsgs() {
    const msgsWithErrStatus = this.ls.get<DelayedPacket[]>(LS.UNSENT_MESSAGES)

    if (msgsWithErrStatus && !!msgsWithErrStatus.length) {
      GD.INVOKE_DELAYED_EVENTS.invoke(msgsWithErrStatus)
    }

    this.ls.set(LS.UNSENT_MESSAGES, [])
  }

  /**
   * For proper code appearing in all devices, must be sent in this format
   * */
  private prepareCodeToSend(txt: string): string {
    const codeLines = txt.match(codeLinesHTML)
    const plainTextLines = codeLines?.map((line, idx) => {
      const isNotLastLine = codeLines.length !== idx + 1
      const matchResult = line.match(codeLineInnerHTML)

      if (matchResult) {
        const [, , codeLineInner] = matchResult
        return isNotLastLine ? codeLineInner + '\n' : codeLineInner
      }

      return line
    })

    return '{code}' + plainTextLines?.join('') + '{code}'
  }

  /**
   * For proper quote appearing in all devices, must be sent in this format
   * */
  private prepareReplyToSend(txt: string): string {
    const quoteData = txt.split(quoteHTML)

    const [, author, created, target, quoteContent, userText] = quoteData

    return `{quote type="quote-type" author="${author}" dt="${created}" target="${target}"}${quoteContent}{quote}${userText}`
  }

  private async prepareMessage(txt: string): Promise<string | null | undefined> {
    const isCode = txt.match(codeHTML)
    const isReply = txt.startsWith('<quote author="')

    txt = txt.replace(/\n{4,}/, '\n\n\n\n') // Combine line breaks if more than 3

    if (isReply) {
      txt = this.prepareReplyToSend(txt)
    }
    else if (isCode) {
      const codeMatch = txt.match(codeHTML)

      if (codeMatch) {
        txt = txt.replace(codeHTML, this.prepareCodeToSend(codeMatch[0]))
      }
    }

    if (txt.length === 0) return

    if (txt.length > 2000) {
      GD.ALERT_ERROR.invoke('The message is too big!')
      return
    }

    txt = DOMPurify.sanitize(txt, { ALLOWED_TAGS: [] })

    const securityKey = await this.getSecurityKey()

    if (!securityKey) return

    return DCCAPI.pack(txt.trim(), securityKey)
  }

  private async getSecurityKey(): Promise<string> {
    return this.chat?.securityKey as string
  }

  private informAboutTyping() {
    GD.WS_SEND.invoke({
      method: WSMessage.BLACK_HOLE,
      data: {
        mode: 'chat',
        chatUID: this.chat?.uid,
        data: {
          method: BlackHoleMethods.USER_WRITING,
          userUID: this.auth?.profile.uid,
          chatUID: this.chat?.uid,
          userName: this.auth?.profile.name,
        }
      }
    })
  }
}

export default Chat
